<!-- EntityHoldings.vue -->
<template>
  <Stack class="stk-py-8">
    <LoaderWithText v-if="!hasFetchedHoldings || isFetchingHoldings">
      Loading…
    </LoaderWithText>

    <!-- Improved Centering for Empty Holdings Panel -->
    <div
      v-else-if="!showHoldingsArea && entityUploadedDfAgg.length === 0"
      class="tw-flex tw-justify-center tw-items-center"
    >
      <Callout status="info" class="tw-p-8">
        <Stack>
          <!-- Panel Header - Improved centering -->
          <div class="tw-mx-auto tw-text-center tw-space-y-2">
            <Title>This client has no holdings</Title>
            <Callout v-if="!canEdit" status="danger">
              You don't have permission to add holdings. Please contact an
              administrator.
            </Callout>
            <Subtitle v-else>
              Choose <em>one</em> of the three methods below to add holdings.
            </Subtitle>
          </div>

          <!-- Cards Container - Improved layout structure -->
          <div
            v-if="canEdit"
            class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-justify-items-center stk-color-text-normal"
          >
            <!-- Copy from Portfolio -->
            <Stack
              gap="4"
              class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
            >
              <div class="tw-flex tw-items-center tw-gap-3">
                <div
                  class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
                >
                  <Icon icon="fas fa-copy" />
                </div>
                <h3 class="tw-font-semibold">Copy Holdings from Portfolio</h3>
              </div>

              <div class="tw-flex-grow">
                <PortfolioSearchSelect
                  placeholder="Search portfolios …"
                  @selected="handleSelected"
                  class="tw-w-full"
                />
              </div>

              <div v-if="copyPortfolioLoading">
                <div class="tw-flex tw-justify-center tw-items-center">
                  Copying holdings from portfolio...
                </div>
                <br />
                <div class="tw-flex tw-justify-center tw-items-center">
                  <i class="fa fa-spinner fa-spin fa-2x tw-text-gray-500"></i>
                </div>
              </div>
            </Stack>

            <!-- Upload CSV -->
            <div class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
              <UploadHoldingsCSV
                :entity="entity"
                :can-edit="canEdit"
                @uploaded="populateEntityHoldings"
              />
            </div>

            <!-- Add Manually -->
            <Stack
              gap="4"
              class="tw-w-full tw-max-w-sm tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4"
            >
              <div class="tw-flex tw-items-center tw-gap-3">
                <div
                  class="tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full"
                >
                  <Icon icon="fas fa-edit" />
                </div>
                <h3 class="tw-font-semibold">Add Manually</h3>
              </div>
              <div
                class="tw-flex-grow tw-flex tw-flex-col tw-items-center tw-justify-center"
              >
                <Stack gap="2" class="tw-text-center">
                  <Button
                    @click="startManualEntry"
                    variant="primary"
                    size="3_5"
                    class="tw-w-full"
                  >
                    Add Holdings Manually
                  </Button>
                  <p class="tw-text-sm tw-text-gray-600 tw-mb-4">
                    Create a new portfolio by adding<br />holdings one by one
                  </p>
                </Stack>
              </div>
            </Stack>
          </div>
        </Stack>
      </Callout>
    </div>

    <div
      v-if="showHoldingsArea"
      class="holdings-date-header"
      style="display: inline-flex; align-items: center; gap: 0.5em"
    >
      <!-- Branch for non-editing mode -->
      <div
        v-if="!editingHoldingsDate"
        class="edit-hover-wrapper"
        style="display: inline-flex; align-items: center; gap: 0.5em"
      >
        <Subtitle> Holdings as of: {{ holdings_as_of_date }} </Subtitle>
        <!-- Only show edit button if hovering, not editing, user can edit, and holdings aren't synced -->
        <Button
          v-if="canEdit && !entity?.holdings_synced"
          @click="editHoldingsDate"
          class="edit-button"
          icon-only="fas fa-edit"
          variant="link"
          size="3"
        ></Button>
      </div>

      <!-- Branch for editing mode -->
      <Buttons v-else>
        <input
          type="date"
          v-model="editedHoldingsDate"
          class="stk-input"
        />
        <Button
          @click="saveHoldingsDate"
          variant="success"
          icon-before="fas fa-save"
        >
          Save
        </Button>
        <Button @click="cancelHoldingsDate" variant="danger">
          Cancel
        </Button>
      </Buttons>
    </div>

    <PortfolioHoldingsArea
      v-if="showHoldingsArea"
      :can_edit="canEdit"
      :entity="entity"
      ref="holdingsArea"
      @holding-saved="handleHoldingSaved"
      @deleted-last-holding="handlePortfolioDeleted"
      @created-first-holding="handlePortfolioCreated"
    />

    <StakeModal v-model="showMultipleUploadInstructions">
      <Stack gap="2">
        <Title>Uploading to multiple members</Title>

        <p>
          To create multiple members, your CSV must
          include a column <code>Account</code> specifying
          which holdings belong to which members.<br />
          Each unique value in that column will represent a
          new member.
        </p>

        <img :src="`${$root.$data.STATIC_ROOT}images/portfolio/csv-upload-multiple.png`">
      </Stack>
    </StakeModal>
  </Stack>
</template>

<script>
import PortfolioHoldingsArea from "@frontend/ui/features/checkup/components/PortfolioHoldingsArea.vue";
import PortfolioSearchSelect from "@frontend/ui/features/checkup/components/portfolio-select/PortfolioSearchSelect.vue";
import { useCheckupStore } from "@frontend/ui/stores/checkupStore";
import { mapStores } from "pinia";
import {
  Button,
  Buttons,
  LoaderWithText,
  Stack,
  Icon,
  StakeModal,
  Dropzone,
  Callout,
  TextLink,
  Title,
  Subtitle,
} from "@stakeui";
import UploadHoldingsCSV from "../../holdings/UploadHoldingsCSV.vue";

export default {
  name: "EntityHoldings",
  components: {
    PortfolioHoldingsArea,
    PortfolioSearchSelect,
    Button,
    Buttons,
    Subtitle,
    LoaderWithText,
    Stack,
    StakeModal,
    Dropzone,
    Callout,
    TextLink,
    Title,
    Icon,
    UploadHoldingsCSV,
  },
  props: {
    entityExternalId: { type: String, required: true },
    entity: { type: Object, required: true },
  },
  data() {
    return {
      hasFetchedHoldings: false,
      isFetchingHoldings: true,
      entityUploadedDfAgg: [],
      showMultipleUploadInstructions: false,
      holdings_as_of_date: this.entity ? this.entity.holdings_as_of_date : null,
      editingHoldingsDate: false,
      editedHoldingsDate: null,
      saveMessage: "",
      uploadName: "",
      showHoldingsArea: false,
      copyPortfolioLoading: false,
      csvUploadLoading: false,
      csvUploadTarget: "direct",
      csvUploadNewMember: "",
    };
  },
  async created() {
    await this.populateEntityHoldings(this.entityExternalId);
  },
  computed: {
    ...mapStores(useCheckupStore),
    headers() {
      return {
        "X-CSRFToken": this.$root.$data.csrfToken,
        "Content-Type": "application/json",
      };
    },
    canEdit() {
      return (
        this.entity &&
        this.entity.user_permissions &&
        this.entity.user_permissions.includes("change_entity")
      );
    },
  },
  watch: {
    entityUploadedDfAgg(newVal) {
      if (newVal && newVal.length) {
        this.showHoldingsArea = true;
      }
    },
  },
  methods: {
    startManualEntry() {
      /* show the table */
      this.showHoldingsArea = true;

      /* wait until it has rendered, then call the child’s method */
      this.$nextTick(() => {
        const child = this.$refs.holdingsArea;
        if (child && typeof child.addNewHolding === "function") {
          child.addNewHolding();
        }
      });
    },
    handleHoldingSaved(newHolding) {
      if (this.entityUploadedDfAgg.length === 0) {
        this.entityUploadedDfAgg.push(newHolding);
      }
    },
    async populateEntityHoldings(externalId, update = true) {
      const url = `/api/v1/entities/${externalId}/holdings`;
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { ...this.headers },
        });
        const data = await res.json();
        this.entityUploadedDfAgg = data.portfolio.uploaded_df_agg;
        if (this.entityUploadedDfAgg && update) {
          const portfolio = await this.checkupStore.getEntityLight(
            this.entityExternalId
          );
          this.checkupStore.addPortfolio({ ...portfolio }, "portfolio");
          this.checkupStore.updateHoldingsByPortfolio();
          this.$emit("refetch-entity");
        }
      } catch (error) {
        console.error("Error fetching holdings:", error);
      } finally {
        this.hasFetchedHoldings = true;
        this.isFetchingHoldings = false;
      }
    },
    handlePortfolioDeleted() {
      this.entityUploadedDfAgg = [];
      this.showHoldingsArea = false;
      this.$emit("refetch-entity");
    },
    handlePortfolioCreated() {
      this.$emit("refetch-entity");
    },
    editHoldingsDate() {
      this.editingHoldingsDate = true;
      this.editedHoldingsDate = this.holdings_as_of_date;
    },
    cancelHoldingsDate() {
      this.editingHoldingsDate = false;
      this.editedHoldingsDate = this.holdings_as_of_date;
    },
    async saveHoldingsDate() {
      try {
        const url = `/api/v1/entities/${this.entityExternalId}/update-entity-field/`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            ...this.headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            holdings_as_of_date: this.editedHoldingsDate,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Update failed.");
        }
        this.holdings_as_of_date = this.editedHoldingsDate;
        this.editingHoldingsDate = false;
        this.saveMessage = "Holdings date updated successfully!";
        setTimeout(() => {
          this.saveMessage = "";
        }, 1500);
      } catch (error) {
        console.error("Error updating holdings date:", error);
        this.saveMessage =
          error.message || "Failed to update holdings date. Try again.";
      }
    },
    async handleSelected(portfolio) {
      if (!this.canEdit) return;

      this.copyPortfolioLoading = true;
      const url = `/api/v1/copy-holdings-to-entity/${this.entityExternalId}/${portfolio.external_id}/`;
      await fetch(url, {
        method: "POST",
        headers: { ...this.headers },
      });
      this.populateEntityHoldings(this.entityExternalId);
      this.copyPortfolioLoading = false;
    },
  },
};
</script>

<style scoped>
.save-message {
  margin-bottom: 1em;
  color: green;
  font-weight: bold;
}

/* Lighten the Callout background to a very pale blue */
.tw-bg-blue-50 {
  background-color: #f0f9ff;
}
.instructions {
  padding: 1.25em 1.5em;
}
.cols {
  display: grid;
  grid-template-columns: 1fr 4em 1fr;
}
.or {
  text-align: center;
}
/* Additional styling for the holdings date header and edit button */
.holdings-date-header {
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
}

.edit-button {
  opacity: 0;
}

.edit-hover-wrapper:hover .edit-button {
  opacity: 1;
}
</style>
